"use client";

import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { cx } from "@frontend/components/ui";

type Tab = "chat" | "details";

/**
 * Shared room chrome: on phones, Chat / Details tabs (Chat first). On desktop
 * the same children sit in the multi-pane layout — one mount, no double polling.
 */
export function CollabRoomFrame({
  rail,
  header,
  chat,
  details,
  needsAction = false,
  preferDetails = false,
  initialTab = null,
}: {
  rail?: ReactNode;
  header: ReactNode;
  chat: ReactNode;
  details: ReactNode;
  /** Badge Details when something needs action there (does not force that tab open). */
  needsAction?: boolean;
  /** Finished / history collabs open on Details. */
  preferDetails?: boolean;
  /** From `?tab=` — wins over defaults until the user switches. */
  initialTab?: Tab | null;
}) {
  const [tab, setTab] = useState<Tab | null>(initialTab);
  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);
  // Open collabs land on Chat; finished/history use Details (`?tab=` wins).
  const active: Tab = tab ?? (preferDetails ? "details" : "chat");

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden rounded-xl border border-line bg-surface lg:flex-row">
      {rail}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="shrink-0">{header}</div>

        {/*
          Tabs sit above the chat/details stack with their own stacking context.
          display:contents + absolute message lists were swallowing taps on iOS.
        */}
        <div
          role="tablist"
          aria-label="Collab sections"
          className="relative z-20 flex shrink-0 border-b border-line bg-surface px-2 lg:hidden"
        >
          <TabButton
            id="chat"
            label="Chat"
            selected={active === "chat"}
            onSelect={() => setTab("chat")}
          />
          <TabButton
            id="details"
            label="Details"
            selected={active === "details"}
            onSelect={() => setTab("details")}
            badge={needsAction}
          />
        </div>

        <div className="relative z-0 flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          {/*
            Keep Chat laid out while Details is open (invisible, not display:none).
            Collapsing the pane breaks scroll-to-bottom on return.
          */}
          <div
            role="tabpanel"
            aria-label="Chat"
            aria-hidden={active !== "chat"}
            className={cx(
              "min-h-0 min-w-0 flex-col overflow-hidden",
              "absolute inset-0 flex lg:static lg:flex-1",
              active === "chat" ? "z-10" : "pointer-events-none invisible z-0 lg:pointer-events-auto lg:visible",
              "lg:flex",
            )}
          >
            {needsAction && active === "chat" ? (
              <button
                type="button"
                onClick={() => setTab("details")}
                className="flex shrink-0 items-center justify-between gap-3 border-b border-line bg-canvas px-3 py-2.5 text-left transition hover:bg-brand-50 lg:hidden"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="size-2 shrink-0 rounded-full bg-money-600" aria-hidden="true" />
                  <span className="truncate text-sm font-medium text-ink">
                    Action needed on Details
                  </span>
                </span>
                <span className="shrink-0 text-xs font-medium text-ink-muted">Open →</span>
              </button>
            ) : null}
            {withChatActive(chat, active === "chat", needsAction)}
          </div>

          <aside
            role="tabpanel"
            aria-label="Details"
            className={cx(
              "relative z-20 min-h-0 w-full flex-col overflow-hidden bg-surface lg:w-[19rem] lg:flex-none lg:border-l lg:border-line",
              active === "details" ? "flex flex-1" : "hidden",
              "lg:flex",
            )}
          >
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
              {details}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

/** Pass Chat focus + action-banner signal into Thread for scroll-to-bottom. */
function withChatActive(
  chat: ReactNode,
  isActive: boolean,
  stickSignal: boolean,
): ReactNode {
  return Children.map(chat, (child) => {
    if (!isValidElement(child)) return child;
    // Chat is often wrapped in <>…</>; rebuild rather than cloneElement on Fragment.
    if (child.type === Fragment) {
      return (
        <Fragment key={child.key}>
          {withChatActive(
            (child.props as { children?: ReactNode }).children,
            isActive,
            stickSignal,
          )}
        </Fragment>
      );
    }
    return cloneElement(
      child as ReactElement<{ isActive?: boolean; stickSignal?: boolean }>,
      { isActive, stickSignal },
    );
  });
}

function TabButton({
  id,
  label,
  selected,
  onSelect,
  badge,
}: {
  id: Tab;
  label: string;
  selected: boolean;
  onSelect: () => void;
  badge?: boolean;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      id={`collab-tab-${id}`}
      onClick={onSelect}
      className={cx(
        "relative z-20 -mb-px flex flex-1 touch-manipulation items-center justify-center gap-1.5 border-b-2 px-3 py-3 text-sm font-medium transition select-none",
        selected ? "border-ink text-ink" : "border-transparent text-ink-muted",
      )}
    >
      {label}
      {badge ? (
        <span
          className="size-1.5 rounded-full bg-money-600"
          aria-label="Action needed"
        />
      ) : null}
    </button>
  );
}
